import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting, useAppStateSelector, pick } from "@nosto/preact"
import { productImagePlaceholder } from "../helpers"
import SubmitButton from "./elements/SubmitButton"
import { useState, useEffect, useRef } from "preact/hooks"
import { pages, popularCollections } from "../helpers"
import DropdownProduct from "./DropdownProduct"
import QuickAdd from "./QuickAdd"

export default function Autocomplete() {
    const { products, keywords } = useResponse();
    const { format } = useCurrencyFormatting();
    const { response, query } = useAppStateSelector(state => pick(state, "response", "query"));
    const [collections, setCollections] = useState([]);
    const parentRef = useRef(null);
    if (!products?.hits?.length && !keywords?.hits?.length) {
        return
    }
    
    const docCount = response.products?.total;
    
    useEffect(() => {
        const body = document.body;

        const toggleBodyClass = () => {
            const parentElement = parentRef.current?.parentNode;
            if (parentElement && getComputedStyle(parentElement).display === "none") {
                body.classList.remove("ns-overflow-hidden");
            } else {
                body.classList.add("ns-overflow-hidden");
            }
        };

        const observer = new MutationObserver(() => {
            toggleBodyClass();
        });

        const parentElement = parentRef.current?.parentNode;
        if (parentElement) {
            observer.observe(parentElement, { attributes: true, attributeFilter: ["style", "class"] });
            toggleBodyClass();
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if(!window.Nosto?.migrateToShopifyMarket) return;
        window.Nosto.migrateToShopifyMarket({
            productSectionElement: '.ns-autocomplete .ns-markets',
            productHandleAttribute: 'data-handle',
            productUrlElement: '.ns-autocomplete .ns-link',
            priceElement: '.ns-autocomplete .ns-price',
            listPriceElement: '.ns-autocomplete .ns-list-price',
        })
    }, [ products?.hits ])

    useEffect(() => {
        const handleClick = (event) => {
            const target = event.target;
            console.log(event.target);
            if (target.tagName === 'BUTTON' && target.closest('modal-opener') && target.closest('.quick-add.no-js-hidden')) {
                event.stopPropagation();
                event.preventDefault();
                const modalOpener = target.closest('modal-opener');
                const modalId = modalOpener.getAttribute('data-modal');
                const quickAddModal = document.querySelector(modalId);
                quickAddModal.show(target);
            }
            
            if (target.tagName === 'BUTTON' && target.closest('.quick-add.no-js-hidden')) {
                event.stopPropagation();
            }
            
            if (target.type === 'submit' && target.closest('.quick-add.no-js-hidden')) {
                event.stopPropagation();
            }
            
            if (parentRef.current && target.closest(".quick-add__submit")) {
                event.stopPropagation();
            }
        };
    
        const autocompleteElement = parentRef.current;
        if (autocompleteElement) {
            autocompleteElement.addEventListener("click", handleClick, true);
        }
    
        return () => {
            if (autocompleteElement) {
                autocompleteElement.removeEventListener("click", handleClick, true);
            }
        };
    }, []);

    useEffect(() => {
        const calculateHeights = () => {
            const sectionHeader = document.querySelector(".section-header");
            const announcementBar = document.querySelector(".announcement-bar-section");
            const headerSearchMobile = document.querySelector(".header-search__inline");

            if (sectionHeader && announcementBar && parentRef.current && window.innerWidth > 989) {
                const sectionHeaderHeight = sectionHeader.offsetHeight || 0;
                const announcementBarHeight = announcementBar.offsetHeight || 0;
                
                const totalHeight = sectionHeaderHeight + announcementBarHeight;

                // parentRef.current.style.top = `${totalHeight}px`;
                parentRef.current.style.maxHeight = `calc(100vh - ${totalHeight}px)`;
            } else if(sectionHeader && announcementBar && parentRef.current && headerSearchMobile && window.innerWidth <= 989){
                const sectionHeaderHeight = sectionHeader.offsetHeight || 0;
                const announcementBarHeight = announcementBar.offsetHeight || 0;
                const headerSearchMobileHeight = headerSearchMobile.offsetHeight || 0;
    
                const totalHeight = sectionHeaderHeight + announcementBarHeight + headerSearchMobileHeight;

                // parentRef.current.style.top = `${totalHeight}px`;
                parentRef.current.style.maxHeight = `calc(100vh - ${totalHeight}px)`;

            }
        };

        calculateHeights();

        window.addEventListener("resize", calculateHeights);

        return () => {
            window.removeEventListener("resize", calculateHeights);
        };
    }, []);
    
    return (
        <div
            class="ns-autocomplete ns-absolute ns-flex-column ns-d-flex ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
            data-nosto-element="autocomplete"
            ref={parentRef}
        >
                <div class="ns-autocomplete-trending-section">
                    <div class="ns-autocomplete-heading ns-uppercase">
                        Trending
                    </div>
                    <div class="ns-d-flex">
                        {(window.nostoPopularSearches || popularSearches).map((item, index)=>{
                            return (
                                <a href={item?.url} class="ns-popular-search ns-d-block">{item?.name}</a>
                            )
                        })}
                    </div>
                </div>
                <div class="ns-d-flex ns-w-100">
                    <div class="ns-autocomplete-searches">
                        <div class="ns-d-flex ns-flex-column">
                            {keywords?.hits?.length > 0 && (
                                <div
                                    class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-autocomplete-section`}
                                >
                                    <div class="ns-autocomplete-heading ns-uppercase">
                                        Suggestions
                                    </div>
                                    <div class="ns-d-flex ns-flex-column">
                                        <Keywords
                                            hits={keywords.hits}
                                            keywordComponent={Keyword}
                                            elementComponent={Element}
                                            keywordInCategoryComponent={KeywordInCategory}
                                        />
                                    </div>
                                </div>
                            )}
                            <div class="ns-autocomplete-section ns-collections-section">
                                <div class="ns-autocomplete-heading ns-uppercase">
                                    Collections
                                </div>
                                <div>
                                {(window?.nostoPopularCollections || popularCollections)?.map((category, index)=>{
                                    return (
                                        <a key={index} href={category?.url} class="ns-suggested-collection ns-d-block">{category?.name}</a>
                                    )
                                })}
                                </div>
                            </div>
                        </div>
                        <div class="ns-autocomplete-section ns-pages-section">
                            <div class="ns-autocomplete-heading ns-uppercase">
                                Pages
                            </div>
                            <div>
                            {(window.nostoPopularPages || pages)?.map((page, index)=>{
                                return (
                                    <a key={index} href={page?.url} class="ns-suggested-page ns-d-block">{page?.name}</a>
                                )
                            })}
                            </div>
                        </div>
                    </div>
                    {products?.hits?.length > 0 && (
                        <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
                            <div class="ns-autocomplete-products-inner">
                                <div class="ns-autocomplete-heading ns-uppercase">
                                    Recommended Products
                                </div>
                                {products?.hits?.map((hit, index) => {
                                    if(hit?.skus?.length > 1){
                                        return <QuickAdd product={hit} key={`quickadd-${hit.productId || index}`} />
                                    } else {
                                        return null;
                                    }
                                })}
                                <ul class="grid grid--3-col grid--2-col-tablet-down ns-autocomplete-products-wrapper">
                                    {products?.hits?.map(hit => {
                                        return (
                                            <DropdownProduct product={hit} key={hit?.productId} />
                                        )
                                    })}
                                </ul>
                            </div>
                            <SubmitButton text={`View all Products`} className='ns-desktop-submit'/>
                        </div>
                    )}
                </div>
                <SubmitButton text={`View all Products`} className='ns-mobile-submit' />
        </div>
    )
}

// initial Autocomplete 
// export default function Autocomplete() {
//     const { products, keywords } = useResponse();
//     const { format } = useCurrencyFormatting();
//     const { response, query } = useAppStateSelector(state => pick(state, "response", "query"));
//     const [collections, setCollections] = useState([]);

//     if (!products?.hits?.length && !keywords?.hits?.length) {
//         return
//     }
    
//     const docCount = response.products?.total;

//     // useEffect(()=>{
//     //     const allCategories = [...new Set(products?.hits?.flatMap(product => product.categories))];
//     //     const searchQuery = query?.query?.toLowerCase();
//     //     // console.log(allCategories);
//     //     const filteredCategories = allCategories.filter(category => category?.toLowerCase().includes(searchQuery));
//     //     setCollections(filteredCategories.slice(0, 3)); 
//     // }, [])
    
//     useEffect(()=>{
//         const body = document.querySelector('body');
//         if(body){
//             body.classList.add('ns-overflow-hidden');
//         }
//         return ()=>{
//             if(body){
//                 body.classList.remove('ns-overflow-hidden');
//             }
//         }
//     }, [])
//     return (
//         <div
//             class="ns-autocomplete ns-absolute ns-d-flex ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
//             data-nosto-element="autocomplete"
//         >
//                 <div class="ns-d-flex ns-w-100">
//                 {products?.hits?.length > 0 && (
//                         <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
//                             <div class="ns-autocomplete-products-inner">
//                                 <div class="ns-autocomplete-heading">
//                                     Products
//                                 </div>
//                                 <ul class="ns-d-flex ns-autocomplete-products-wrapper">
//                                     {products?.hits?.map(hit => {
//                                         return (
//                                             <li>
//                                                 <AutocompleteElement key={hit.productId} hit={hit}>
//                                                     <div
//                                                         data-url={hit.url}
//                                                         class="ns-d-flex ns-clickable"
//                                                         data-nosto-element="product"
//                                                     >
//                                                         <div class="ns-autocomplete-product-media">
//                                                             <img
//                                                                 class="ns-h-auto ns-w-100 ns-object-contain"
//                                                                 src={hit.imageUrl ?? productImagePlaceholder}
//                                                                 alt={hit.name}
//                                                             />
//                                                         </div>
//                                                         <div class="ns-autocomplete-product-info">
//                                                             <div class="ns-title">
//                                                                 {hit.name}
//                                                             </div>
//                                                             {hit.brand && (
//                                                                 <div class="ns-brand">{hit.brand}</div>
//                                                             )}
//                                                             <div class="ns-price-wrapper">
//                                                                 <span class={`ns-price ${hit.listPrice > hit.price ? 'ns-sale-price':''}`}>{format(hit.price)}</span>
//                                                                 {hit.listPrice && hit.listPrice > hit.price && (
//                                                                     <span class="ns-list-price ns-text-striked">
//                                                                         {format(hit.listPrice)}
//                                                                     </span>
//                                                                 )}
//                                                             </div>
//                                                         </div>
//                                                     </div>
//                                                 </AutocompleteElement>
//                                             </li>
//                                         )
//                                     })}
//                                 </ul>
//                             </div>
//                             <SubmitButton text={`View All ${docCount} Products`} className='ns-desktop-submit'/>
//                         </div>
//                     )}
//                     <div class="ns-autocomplete-searches">
//                         {keywords?.hits?.length > 0 && (
//                             <div
//                                 class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-autocomplete-section`}
//                             >
//                                 <div class="ns-autocomplete-heading">
//                                     Popular Suggestions
//                                 </div>
//                                 <div class="ns-d-flex ns-flex-column">
//                                     <Keywords
//                                         hits={keywords.hits}
//                                         keywordComponent={Keyword}
//                                         elementComponent={Element}
//                                         keywordInCategoryComponent={KeywordInCategory}
//                                     />
//                                 </div>
//                             </div>
//                         )}
//                         <div class="ns-autocomplete-section">
//                             <div class="ns-autocomplete-heading">
//                                 Collections
//                             </div>
//                             <div>
//                             {(window?.nostoPopularCollections || popularCollections)?.map((category, index)=>{
//                                 return (
//                                     <a key={index} href={category?.url} class="ns-suggested-collection ns-d-block">{category?.name}</a>
//                                 )
//                             })}
//                             </div>
//                         </div>
//                         <div class="ns-autocomplete-section">
//                             <div class="ns-autocomplete-heading">
//                                 Blogs & Pages
//                             </div>
//                             <div>
//                             {(window.nostoPopularPages || pages)?.map((page, index)=>{
//                                 return (
//                                     <a key={index} href={page?.url} class="ns-suggested-page ns-d-block">{page?.name}</a>
//                                 )
//                             })}
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//                 <SubmitButton text={`View All ${docCount} Products`} className='ns-mobile-submit' />
//         </div>
//     )
// }
// ------ 

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class="ns-clickable">
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}
